/**
 * 전역 이미지 캐시
 * 게임 시작 전에 모든 이미지를 로딩하고, 각 게임에서 재사용할 수 있도록 관리합니다.
 */

class GlobalImageCache {
  constructor() {
    this.cache = new Map();
    this.isReady = false;
  }

  /**
   * 이미지를 캐시에 저장
   * @param {string} url - 이미지 URL
   * @param {Image} imageObject - 로딩된 Image 객체
   */
  set(url, imageObject) {
    this.cache.set(url, imageObject);
  }

  /**
   * 캐시에서 이미지를 가져옴
   * @param {string} url - 이미지 URL
   * @returns {Image|null} 캐시된 Image 객체 또는 null
   */
  get(url) {
    return this.cache.get(url) || null;
  }

  /**
   * URL이 캐시에 있는지 확인
   * @param {string} url - 이미지 URL
   * @returns {boolean}
   */
  has(url) {
    return this.cache.has(url);
  }

  /**
   * 캐시된 이미지가 사용 가능한지 확인 (로딩 완료 상태)
   * @param {string} url - 이미지 URL
   * @returns {boolean}
   */
  isAvailable(url) {
    const img = this.cache.get(url);
    return img && img.complete && img.naturalHeight !== 0;
  }

  /**
   * 모든 캐시 삭제
   */
  clear() {
    this.cache.clear();
    this.isReady = false;
  }

  /**
   * 캐시 준비 상태 설정
   * @param {boolean} ready - 준비 상태
   */
  setReady(ready) {
    this.isReady = ready;
  }

  /**
   * 캐시가 준비되었는지 확인
   * @returns {boolean}
   */
  isReadyToUse() {
    return this.isReady;
  }

  /**
   * 캐시 상태 정보
   * @returns {object} 캐시 상태 정보
   */
  getStatus() {
    const total = this.cache.size;
    const loaded = Array.from(this.cache.values()).filter(img => 
      img && img.complete && img.naturalHeight !== 0
    ).length;
    
    return {
      total,
      loaded,
      progress: total > 0 ? loaded / total : 0,
      isReady: this.isReady
    };
  }

  /**
   * 디버깅용 - 캐시된 모든 URL 목록
   * @returns {string[]} URL 목록
   */
  getCachedUrls() {
    return Array.from(this.cache.keys());
  }
}

// 전역 싱글톤 인스턴스
const globalImageCache = new GlobalImageCache();

export default globalImageCache;
